/**
 * The "where does this apply?" step (spec 2026-10-02 §5.3; mock artboards 3,
 * 4, 7). It replaces the todo form's footer — inside the same sheet, never a
 * second modal — with one button per choice, each carrying a one-line
 * consequence, plus Back. For a delete the choice IS the confirmation.
 */
import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import type { ScopeChoice } from '@/utils/residents/todoScope';
import type { EditScope } from '@/types/residents/todos';

interface ScopeChoicesProps {
  heading: string;
  choices: ScopeChoice[];
  /** Delete: error ink on the error tint. */
  destructive?: boolean;
  busy: boolean;
  onChoose: (scope: EditScope) => void;
  onBack: () => void;
}

export const ScopeChoices: React.FC<ScopeChoicesProps> = ({
  heading,
  choices,
  destructive = false,
  busy,
  onChoose,
  onBack,
}) => {
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      list: {
        gap: space.sm,
      },
      heading: {
        ...type.small,
        color: semantic.text.secondary,
        paddingBottom: space.xs,
      },
      choice: {
        gap: 2,
        paddingHorizontal: space.lg,
        paddingVertical: space.md,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.card.border,
        backgroundColor: colors.glass.buttonBg,
      },
      choiceDestructive: {
        backgroundColor: semantic.status.errorBg,
      },
      choiceName: {
        ...type.button,
        color: colors.text.primary,
      },
      choiceNameDestructive: {
        color: semantic.status.errorText,
      },
      caption: {
        ...type.caption,
        color: semantic.text.secondary,
      },
      backRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingTop: space.xs,
      },
      // Same padded box as the form's Cancel: the word stays put, the hover
      // tint gets room around it.
      back: {
        paddingHorizontal: space.sm,
        paddingVertical: space.xs,
        marginHorizontal: -space.sm,
        marginVertical: -space.xs,
        borderRadius: radius.md,
      },
      backText: {
        ...type.body,
        color: colors.text.secondary,
      },
    }),
  );

  return (
    <View style={styles.list}>
      <Text style={styles.heading}>{heading}</Text>
      {choices.map(choice => (
        <Hoverable
          key={choice.scope}
          // A destructive choice rests on an OPAQUE error tint, so `glass` (it writes its own fill
          // on hover) would swap that tint for a neutral one and leave the error ink on it at
          // 3.76:1 in dark. `button` is the kind for opaque fills: the tint stays, the pointer
          // cursor stays (`field` would leave it alone), and the hover shows as a shadow + lift.
          hover={destructive ? 'button' : 'glass'}
          style={[styles.choice, destructive && styles.choiceDestructive]}
          onPress={() => onChoose(choice.scope)}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel={`${choice.label}. ${choice.caption}`}
          accessibilityState={{ disabled: busy }}
        >
          <Text style={[styles.choiceName, destructive && styles.choiceNameDestructive]}>
            {choice.label}
          </Text>
          <Text style={styles.caption} scale="content">
            {choice.caption}
          </Text>
        </Hoverable>
      ))}
      <View style={styles.backRow}>
        <Hoverable
          hover="row"
          style={styles.back}
          onPress={onBack}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel="Back to the form"
        >
          <Text style={styles.backText}>Back</Text>
        </Hoverable>
      </View>
    </View>
  );
};

ScopeChoices.displayName = 'ScopeChoices';
